import { CustomHeader } from "@/components/CustomHeader";
import { FeedSkeleton } from "@/components/home/FeedSkeleton.web";
import {
  HOME_TAB_SWITCHER_HEIGHT,
  HomeTabSwitcher,
  HomeTabValue,
} from "@/components/home/HomeTabSwitcher.web";
import { PostsMap } from "@/components/home/PostsMap.web";
import { PhoneVerifyBanner } from "@/components/PhoneVerifyBanner";
import { PostCard } from "@/components/PostCard";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import VehiLinkIcon from "@/components/ui/VehiLinkIcon";
import { VisuallyHiddenHeading } from "@/components/ui/VisuallyHiddenHeading.web";
import { useAuth } from "@/context/AuthContext";
import { usePosts } from "@/context/PostsContext";
import { useScrollHideProgress } from "@/context/ScrollHideContext";
import { VideoVisibilityProvider } from "@/context/VideoVisibilityContext";
import { useThemeColor } from "@/hooks/useThemeColor";
import { DEFAULT_RECENCY, MapPostsFilters } from "@/types/mapPostsFilters";
import type { Post } from "@/types/post";
import Ionicons from "@expo/vector-icons/Ionicons";
import { useFocusEffect } from "@react-navigation/native";
import { router } from "expo-router";
import Head from "expo-router/head";
import { StatusBar } from "expo-status-bar";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Animated,
  FlatList,
  ListRenderItemInfo,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Pressable,
  StyleSheet,
  TouchableOpacity,
} from "react-native";

const BASE_HEADER_HEIGHT = 60;
const HEADER_HEIGHT = BASE_HEADER_HEIGHT + HOME_TAB_SWITCHER_HEIGHT;
const TAB_BAR_HEIGHT = 40;
const SCROLL_DELTA_THRESHOLD = 4;

function HomeScreenInner() {
  const { user } = useAuth();
  const {
    posts,
    initialLoading,
    backgroundRefreshing,
    loadingMore,
    fetchMorePosts,
    refreshIfStale,
    refreshVisibleCounts,
  } = usePosts();
  const tintColor = useThemeColor({}, "tint");
  const stripesColor = useThemeColor({}, "stripes");
  const backgroundColor = useThemeColor({}, "background");
  const { t } = useTranslation();

  const progress = useScrollHideProgress();
  const lastScrollY = useRef(0);
  const headerVisible = useRef(true);

  const [activeTab, setActiveTab] = useState<HomeTabValue>("feed");
  const [mapFilters, setMapFilters] = useState<MapPostsFilters>({
    recency: DEFAULT_RECENCY,
    city: null,
    eventType: null,
  });
  const [fabOpen, setFabOpen] = useState(false);
  const fabAnim = useRef(new Animated.Value(0)).current;

  // Header + tab switcher + (conditionally) the phone-verify banner all live
  // inside one Animated.View. Its rendered height varies with banner
  // visibility and text wrap, so onLayout feeds the actual measured height
  // into translateY / paddingTop — otherwise the banner would leave a
  // visible sliver whenever the header retracts on scroll.
  const [headerBlockHeight, setHeaderBlockHeight] = useState(HEADER_HEIGHT);

  const headerTranslateY = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [0, -headerBlockHeight],
  });
  const contentPaddingTop = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [headerBlockHeight, 0],
  });
  const fabOpacity = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 0.4],
  });

  const animateTo = (toValue: number) => {
    Animated.timing(progress, {
      toValue,
      duration: 200,
      useNativeDriver: false,
    }).start();
  };

  useEffect(() => {
    return () => {
      progress.setValue(0);
    };
  }, [progress]);

  useEffect(() => {
    Animated.timing(fabAnim, {
      toValue: fabOpen ? 1 : 0,
      duration: 180,
      useNativeDriver: false,
    }).start();
  }, [fabOpen, fabAnim]);

  const closeFab = useCallback(() => setFabOpen(false), []);

  const handleCreatePost = useCallback(() => {
    closeFab();
    if (!user) {
      router.push("/(auth)/login");
      return;
    }
    router.push({
      pathname: "/(tabs)/post/new",
      params: { targetName: t("GLOBAL_FEED") },
    });
  }, [closeFab, user, t]);

  const handleReportNearby = useCallback(() => {
    closeFab();
    if (!user) {
      router.push("/(auth)/login");
      return;
    }
    router.push("/(tabs)/report/area");
  }, [closeFab, user]);

  const mainFabRotate = fabAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "45deg"],
  });
  const subFabTranslateY = fabAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [20, 0],
  });

  // Returning to the feed screen (from a post detail, another tab, etc.)
  // silently pulls newer posts (if cache is stale) AND patches like/comment
  // counts on the top visible posts — D1 dropped the per-card listeners, so
  // this is what keeps visible counts from going stale on long sessions.
  useFocusEffect(
    useCallback(() => {
      void refreshIfStale();
      void refreshVisibleCounts();
    }, [refreshIfStale, refreshVisibleCounts]),
  );

  // Stable renderItem keeps FlatList's internal item-renderer cache valid across
  // re-renders of the parent — pairs with the React.memo on PostCard so cards
  // whose post reference is unchanged skip work entirely.
  const renderPost = useCallback(
    ({ item, index }: ListRenderItemInfo<Post>) => (
      <PostCard post={item} isLCPCandidate={index === 0} />
    ),
    [],
  );

  useEffect(() => {
    if (activeTab === "map") {
      progress.setValue(0);
      headerVisible.current = true;
      lastScrollY.current = 0;
    }
  }, [activeTab, progress]);

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const currentY = event.nativeEvent.contentOffset.y;
    const diff = currentY - lastScrollY.current;

    if (currentY <= 0) {
      if (!headerVisible.current) {
        headerVisible.current = true;
        animateTo(0);
      }
    } else if (diff > SCROLL_DELTA_THRESHOLD && headerVisible.current) {
      headerVisible.current = false;
      animateTo(1);
    } else if (diff < -SCROLL_DELTA_THRESHOLD && !headerVisible.current) {
      headerVisible.current = true;
      animateTo(0);
    }

    lastScrollY.current = currentY;
  };

  return (
    <ThemedView style={styles.container}>
      <Head>
        <title>{t("PAGE_TITLE_FEED")}</title>
      </Head>
      <VisuallyHiddenHeading>{t("LANDING_H1")}</VisuallyHiddenHeading>
      <Animated.View
        onLayout={(e) => {
          const h = e.nativeEvent.layout.height;
          if (h > 0 && h !== headerBlockHeight) setHeaderBlockHeight(h);
        }}
        style={[
          styles.header,
          { transform: [{ translateY: headerTranslateY }] },
        ]}
      >
        <CustomHeader
          childrenStyle={{ justifyContent: "center", alignItems: "center" }}
        >
          <VehiLinkIcon size={48} />
        </CustomHeader>
        <HomeTabSwitcher value={activeTab} onChange={setActiveTab} />
        <PhoneVerifyBanner />
      </Animated.View>
      <StatusBar style="dark" animated={true} />
      <Animated.View
        style={[styles.contentWrapper, { paddingTop: contentPaddingTop }]}
      >
        {activeTab === "feed" ? (
          <ThemedView
            style={[styles.listContainer, { backgroundColor: stripesColor }]}
          >
            {backgroundRefreshing && posts.length > 0 && (
              <ThemedView
                style={[styles.bgRefreshBar, { backgroundColor: tintColor }]}
                pointerEvents="none"
              />
            )}
            <VideoVisibilityProvider>
              <FlatList
                data={posts}
                renderItem={renderPost}
                keyExtractor={(item) => item.id}
                style={[styles.flatListStyle, { backgroundColor }]}
                contentContainerStyle={styles.flatListContent}
                onEndReached={fetchMorePosts}
                onEndReachedThreshold={0.5}
                onScroll={handleScroll}
                scrollEventThrottle={16}
                initialNumToRender={5}
                maxToRenderPerBatch={5}
                windowSize={7}
                removeClippedSubviews={false}
                ListEmptyComponent={
                  initialLoading ? (
                    <FeedSkeleton />
                  ) : (
                    <ThemedText style={{ textAlign: "center", marginTop: 20 }}>
                      {t("NO_POSTS_FOUND")}
                    </ThemedText>
                  )
                }
                ListFooterComponent={
                  loadingMore ? (
                    <ActivityIndicator size="large" color={tintColor} />
                  ) : null
                }
              />
            </VideoVisibilityProvider>
          </ThemedView>
        ) : (
          <PostsMap filters={mapFilters} onFiltersChange={setMapFilters} />
        )}
      </Animated.View>
      {!user && (
        <ThemedView style={styles.guestBannerBottom}>
          <ThemedText style={styles.guestBannerText}>
            {t("PLEASE_LOGIN_TO_INTERACT")}
          </ThemedText>
          <TouchableOpacity
            onPress={() => router.push("/(auth)/login")}
            style={[styles.guestBannerButton, { backgroundColor: tintColor }]}
          >
            <ThemedText style={styles.guestBannerButtonText}>
              {t("LOGIN")}
            </ThemedText>
          </TouchableOpacity>
        </ThemedView>
      )}
      {fabOpen && <Pressable style={styles.fabOverlay} onPress={closeFab} />}
      <Animated.View
        pointerEvents="box-none"
        style={[
          styles.fabContainer,
          !user && styles.fabContainerGuestOffset,
          { opacity: fabOpacity },
        ]}
      >
        <Animated.View
          pointerEvents={fabOpen ? "auto" : "none"}
          style={[
            styles.fabSubRow,
            styles.fabSubRowReport,
            {
              opacity: fabAnim,
              transform: [{ translateY: subFabTranslateY }],
            },
          ]}
        >
          <ThemedView style={styles.fabSubLabel}>
            <ThemedText style={styles.fabSubLabelText}>
              {t("FAB_REPORT_NEARBY")}
            </ThemedText>
          </ThemedView>
          <TouchableOpacity
            style={styles.fabSub}
            onPress={handleReportNearby}
            accessibilityLabel={t("FAB_REPORT_NEARBY")}
          >
            <Ionicons name="shield-checkmark-outline" size={22} color="white" />
          </TouchableOpacity>
        </Animated.View>

        <Animated.View
          pointerEvents={fabOpen ? "auto" : "none"}
          style={[
            styles.fabSubRow,
            styles.fabSubRowCreate,
            {
              opacity: fabAnim,
              transform: [{ translateY: subFabTranslateY }],
            },
          ]}
        >
          <ThemedView style={styles.fabSubLabel}>
            <ThemedText style={styles.fabSubLabelText}>
              {t("FAB_CREATE_POST")}
            </ThemedText>
          </ThemedView>
          <TouchableOpacity
            style={styles.fabSub}
            onPress={handleCreatePost}
            accessibilityLabel={t("FAB_CREATE_POST")}
            testID="fab-create-post"
          >
            <Ionicons name="document-text-outline" size={22} color="white" />
          </TouchableOpacity>
        </Animated.View>

        <TouchableOpacity
          style={styles.fab}
          onPress={() => setFabOpen((o) => !o)}
          accessibilityLabel={t("FAB_CREATE_POST")}
          testID="fab-main"
        >
          <Animated.View style={{ transform: [{ rotate: mainFabRotate }] }}>
            <Ionicons name="add" size={30} color="white" />
          </Animated.View>
        </TouchableOpacity>
      </Animated.View>
    </ThemedView>
  );
}

export default function HomeScreen() {
  return <HomeScreenInner />;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 20,
  },
  contentWrapper: {
    flex: 1,
  },
  listContainer: {
    flex: 1,
    alignItems: "center",
  },
  // Thin shimmer bar pinned to the top edge of the list while a silent refresh
  // is in flight. Absolute-positioned so it never shifts feed content.
  bgRefreshBar: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 2,
    opacity: 0.7,
    zIndex: 5,
  },
  flatListStyle: {
    flex: 1,
    width: "100%",
    maxWidth: 680,
  },
  flatListContent: {
    paddingBottom: TAB_BAR_HEIGHT,
  },
  fabContainer: {
    position: "absolute",
    width: 56,
    height: 56,
    right: 20,
    bottom: 20 + TAB_BAR_HEIGHT,
    zIndex: 10,
  },
  fabContainerGuestOffset: {
    bottom: 96 + TAB_BAR_HEIGHT,
  },
  fab: {
    width: 56,
    height: 56,
    backgroundColor: "#1e3a8a",
    borderRadius: 28,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 4.65,
  },
  fabTouchable: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  fabSubRow: {
    position: "absolute",
    right: 4,
    width: 240,
    height: 48,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
  },
  fabSubRowCreate: {
    bottom: 70,
  },
  fabSubRowReport: {
    bottom: 130,
  },
  fabSub: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: "#1e3a8a",
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.25,
    shadowRadius: 3.5,
  },
  fabSubLabel: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: "white",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 2,
  },
  fabSubLabelText: {
    fontSize: 13,
    fontWeight: "600",
  },
  fabOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "transparent",
    zIndex: 9,
  },
  guestBannerBottom: {
    position: "absolute",
    left: 16,
    right: 16,
    bottom: 20 + TAB_BAR_HEIGHT,
    zIndex: 9,
    borderWidth: 1,
    borderColor: "#d9d9d9",
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  guestBannerText: {
    flex: 1,
    fontSize: 13,
  },
  guestBannerButton: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 999,
  },
  guestBannerButtonText: {
    color: "white",
    fontWeight: "700",
  },
});
